import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './ui';

// =============================================================================
// ContextMenu — a reusable, portaled, viewport-aware right-click menu.
//
// Portaled to document.body (like UserPopover) so it escapes clipping/opacity
// from its trigger's ancestors. Opens at the cursor and flips left/up when it
// would overflow the viewport. Dismisses on outside-click, Escape, scroll,
// resize, or a second right-click elsewhere.
//
// Usage:
//   const cm = useContextMenu();
//   <div onContextMenu={cm.open}>…</div>
//   {cm.pos && <ContextMenu x={cm.pos.x} y={cm.pos.y} onClose={cm.close} items={[…]} />}
// =============================================================================

export type ContextMenuItem =
  | { type: 'separator' }
  | {
      type?: 'item';
      label: string;
      icon?: string;
      onClick: () => void;
      danger?: boolean;
      disabled?: boolean;
    };

export function useContextMenu() {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const open = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPos({ x: e.clientX, y: e.clientY });
  }, []);
  const close = useCallback(() => setPos(null), []);
  return { pos, open, close };
}

export function ContextMenu({
  x,
  y,
  items,
  onClose,
}: {
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number }>({ left: x, top: y });

  // Measure then flip/shift to stay on screen. Runs before paint so the menu
  // never visibly jumps from the raw cursor point to the clamped point.
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const { width, height } = el.getBoundingClientRect();
    const pad = 8;
    let left = x;
    let top = y;
    if (left + width > window.innerWidth - pad) left = Math.max(pad, x - width);
    if (top + height > window.innerHeight - pad) top = Math.max(pad, y - height);
    setPos({ left, top });
  }, [x, y, items]);

  // Dismiss on outside-click, Escape, scroll, resize, or right-click elsewhere.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    // Capture phase so a scroll anywhere (incl. the message list) closes it.
    window.addEventListener('mousedown', onDown, true);
    window.addEventListener('contextmenu', onDown, true);
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      window.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('contextmenu', onDown, true);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      role="menu"
      className="fixed z-100 min-w-[180px] py-1 bg-elevated border border-border-soft rounded-lg shadow-[0_8px_24px_rgba(0,0,0,0.22)] text-sm select-none"
      style={{ left: pos.left, top: pos.top }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map((item, i) => {
        if ('type' in item && item.type === 'separator') {
          return <div key={i} className="my-1 h-px bg-border-soft" />;
        }
        const it = item as Extract<ContextMenuItem, { label: string }>;
        return (
          <button
            key={i}
            type="button"
            role="menuitem"
            disabled={it.disabled}
            onClick={() => {
              if (it.disabled) return;
              onClose();
              it.onClick();
            }}
            className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left transition-colors disabled:opacity-40 disabled:cursor-default ${
              it.danger
                ? 'text-danger hover:bg-danger/15'
                : 'text-text-muted hover:bg-accent/20 hover:text-text'
            }`}
          >
            {it.icon && <Icon name={it.icon} size={14} className="shrink-0" />}
            <span className="truncate">{it.label}</span>
          </button>
        );
      })}
    </div>,
    document.body
  );
}
